import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Card } from '@/components/ui/Card';
import { ListItem } from '@/components/ui/ListItem';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { useAutoBookingSettings, useMe, useUpdateAutoBookingSettings } from '@/api/queries/user';
import { useFamily } from '@/api/queries/family';
import { paths } from '@/routes/paths';
import { useAuth } from '@/hooks/useAuth';
import type { TicketClass } from '@/api/types';
import styles from './styles.module.css';

export function SettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useMe();
  const settings = useAutoBookingSettings();
  const update = useUpdateAutoBookingSettings();
  const family = useFamily();
  const { logout } = useAuth();

  const cls: TicketClass = settings.data?.ticket_class ?? 'economy';

  const updateClass = (v: TicketClass) => update.mutate({ ticket_class: v });
  const updateFamilyMode = (v: boolean) => update.mutate({ family_safety_mode: v });
  const updateAutomation = (v: boolean) => update.mutate({ enabled: v });

  return (
    <AppShell>
      <PageContainer paddingX="md">
        <TopBar
          title={t('settings.title')}
          bell
          leading={<div className={styles.avatar} />}
          onBellClick={() => navigate(paths.notifications)}
        />

        <Card variant="light" padding="md">
          <div className={styles.h}>{t('settings.automation.title')}</div>
          <p className={styles.sub}>{t('settings.automation.subtitle')}</p>
          <Toggle
            label={t('settings.automation.title')}
            checked={!!settings.data?.enabled}
            onChange={(e) => updateAutomation(e.target.checked)}
          />
        </Card>

        <Card variant="light" padding="md">
          <ListItem
            title={t('settings.evacuation_triggers.title')}
            subtitle={
              settings.data?.evacuation_triggers?.length
                ? t('settings.evacuation_triggers.included', {
                    list: settings.data.evacuation_triggers
                      .map((c) => t(`triggers.${c}`))
                      .join(', '),
                  })
                : t('settings.evacuation_triggers.none')
            }
            trailing="▾"
            onClick={() => navigate(paths.settingsEvacuation)}
          />
        </Card>

        <Card variant="light" padding="md">
          <div className={styles.h}>{t('settings.booking_priorities')}</div>
          <ListItem
            title={t('settings.end_point')}
            subtitle={t('settings.home_country')}
            onClick={() => navigate(paths.settingsEvacuation)}
            trailing="▾"
          />
          <ListItem
            title={t('settings.additional_countries')}
            subtitle={t('settings.priority_edit.subtitle')}
            onClick={() => navigate(paths.settingsPriorityEdit)}
            trailing="▾"
          />
          <div className={styles.h} style={{ marginTop: 12 }}>{t('settings.service_class')}</div>
          <div className={styles.segmented}>
            {(['premium', 'business', 'economy'] as TicketClass[]).map((c) => (
              <button
                key={c}
                className={`${styles.seg} ${cls === c ? styles.segActive : ''}`}
                onClick={() => updateClass(c)}
              >
                {t(`booking.class.${c}`)}
              </button>
            ))}
          </div>
        </Card>

        <Card variant="light" padding="md">
          <div className={styles.h}>{t('settings.family_safety_mode.title')}</div>
          <p className={styles.sub}>{t('settings.family_safety_mode.subtitle')}</p>
          <Toggle
            label={t('settings.family_safety_mode.title')}
            checked={!!settings.data?.family_safety_mode}
            onChange={(e) => updateFamilyMode(e.target.checked)}
          />
        </Card>

        <Card variant="light" padding="md">
          <div className={styles.h}>{t('settings.family')}</div>
          {family.data?.map((m) => (
            <ListItem
              key={m.id}
              title={`${m.given_name} ${m.surname}`}
              subtitle={t(`family_member.role.${m.role}`)}
              onClick={() => navigate(paths.settingsFamilyEdit)}
            />
          ))}
          <Button block variant="ghost" onClick={() => navigate(paths.settingsFamilyEdit)}>
            + {t('onboarding.family.add')}
          </Button>
        </Card>

        <Card variant="light" padding="md">
          <ListItem
            title={t('settings.language')}
            subtitle={t(`settings.languages.${me.data?.locale ?? 'en'}`)}
            trailing="▾"
            onClick={() => navigate(paths.settingsLanguage)}
          />
          <ListItem
            title={t('settings.support')}
            onClick={() => navigate(paths.support)}
            trailing="▾"
          />
          <ListItem
            title={t('settings.privacy')}
            onClick={() => navigate(paths.settingsPolicies)}
            trailing="▾"
          />
          <ListItem
            title={t('settings.user_agreement')}
            onClick={() => navigate(paths.settingsPolicies)}
            trailing="▾"
          />
        </Card>

        <Button block variant="danger" onClick={logout}>
          {t('auth.logout')}
        </Button>
      </PageContainer>
    </AppShell>
  );
}
